package it.neckar.lizergy.model.financial.quote.query

import it.neckar.commons.tags.Tag
import it.neckar.lizergy.model.financial.quote.Optionality
import it.neckar.lizergy.model.financial.quote.QuoteElement
import it.neckar.lizergy.model.financial.quote.Visibility

/**
 * Describes a query for relevance categories.
 * The query can be used to calculate sums.
 */
interface VisibilityQuery : Query {
  /**
   * Creates a new query that connects both queries using "and"
   */
  operator fun plus(otherQuery: VisibilityQuery): VisibilityQuery {
    return AndVisibilityQuery(listOf(this, otherQuery))
  }


  companion object {
    /**
     * Returns the visible and mandatory items.
     */
    val allVisibleOnlyMandatory: VisibilityQuery = object : VisibilityQuery {
      override fun matches(quoteElement: QuoteElement): Boolean {
        return onlyMandatory.matches(quoteElement) && onlyPublic.matches(quoteElement)
      }
    }

    /**
     * Filters all visible elements - including optional elements.
     * This query should typically be used to decide which elements are shown
     */
    val onlyPublic: VisibilityQuery = object : VisibilityQuery {
      override fun matches(quoteElement: QuoteElement): Boolean {
        return quoteElement.visibility == Visibility.Public
      }
    }

    /**
     * Selects all items. Even the optional.
     * Must not be used for calculations.
     */
    val allIncludingOptional: VisibilityQuery = object : VisibilityQuery {
      override fun matches(quoteElement: QuoteElement): Boolean {
        return true
      }
    }

    val onlyOptional: VisibilityQuery = object : VisibilityQuery {
      override fun matches(quoteElement: QuoteElement): Boolean {
        return quoteElement.optionality == Optionality.Optional
      }
    }

    /**
     * Only mandatory items are returned (not optional)
     *
     *
     * This query should typically be used to calculate the sum
     */
    val onlyMandatory: VisibilityQuery = object : VisibilityQuery {
      override fun matches(quoteElement: QuoteElement): Boolean {
        return (quoteElement.optionality == Optionality.Mandatory)
      }
    }

    /**
     * Skips all items that have a sum of 0.0
     */
    val skipZero: VisibilityQuery = object : VisibilityQuery {
      override fun matches(quoteElement: QuoteElement): Boolean {
        return quoteElement.netPrice.isZero().not()
      }
    }

    /**
     * Shows only public entries - skip elements with salces price of "0.0"
     */
    val onlyPublicSkipZero: VisibilityQuery = object : VisibilityQuery {
      override fun matches(quoteElement: QuoteElement): Boolean {
        return onlyPublic.matches(quoteElement) && skipZero.matches(quoteElement)
      }
    }

    /**
     * The default query for visualization for the consumer
     */
    val default: VisibilityQuery = onlyPublic


    fun onlyMandatoryWithoutTag(tag: Tag): VisibilityQuery {
      return object : VisibilityQuery {
        override fun matches(quoteElement: QuoteElement): Boolean {
          return onlyMandatory.matches(quoteElement) && (quoteElement.relevanceOrNull()?.contains(tag)?.not() == true)
        }
      }
    }

  }
}

/**
 * Returns true if all the delegates returns true.
 * Returns false if any of the delegates returns false.
 */
data class AndVisibilityQuery(override val delegates: List<VisibilityQuery>) : AndQuery, VisibilityQuery

/**
 * Returns true if all the delegates return false.
 * Returns false if at least one of the delegates returns true
 */
data class NoneVisibilityQuery(override val delegates: List<VisibilityQuery>) : NoneQuery, VisibilityQuery
